import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 pt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold text-sm transition-colors mb-6">
            <ArrowLeft size={18} />
            Back to Valuation
          </Link>
          <div className="text-center mb-4">
            <img src="sell-my-car-teesside-logo.png" alt="Sell My Car Teesside Logo" className="mx-auto w-48 h-24 mb-3 object-contain" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 text-center">Privacy Policy</h1>
          <p className="text-slate-500 text-sm text-center">Last updated: February 2026</p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200 mb-8 space-y-8 text-slate-700 text-sm leading-relaxed">
          <section>
            <p className="mb-3">ICO Reference: ZB732531</p>
            <p>TeessideAutos Ltd is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal data, and outlines your rights under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">1. Who We Are</h2>
            <div className="space-y-1">
              <p><strong>Data Controller:</strong> TeessideAutos Ltd</p>
              <p><strong>Address:</strong> 8&ndash;10 Hume Street, Stockton-On-Tees, TS18 2ER</p>
              <p><strong>Phone:</strong> 07948 089617</p>
              <p><strong>Email:</strong> teessideautos1@gmail.com</p>
            </div>
            <p className="mt-3">If you have any questions or concerns about how we handle your data, please contact us using the details above.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">2. What Data We Collect</h2>
            <p className="mb-2">We may collect and process the following personal data:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Full name</li>
              <li>Contact details (address, email, phone)</li>
              <li>Date of birth</li>
              <li>Employment and income information (for finance applications)</li>
              <li>Vehicle details (e.g. registration number)</li>
              <li>Identification documents (e.g. driving licence)</li>
              <li>Website usage data (if you contact us online)</li>
              <li>CCTV footage (if you visit our premises)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">3. Legal Basis for Processing Your Data</h2>
            <p className="mb-3">We process personal data under the following lawful bases:</p>

            <h3 className="font-semibold text-slate-900 mb-2">a) Contract</h3>
            <p className="mb-1">We process your data when necessary to:</p>
            <ul className="list-disc ml-6 space-y-1 mb-4">
              <li>Respond to enquiries and complete the sale or purchase of a vehicle, goods, or services</li>
              <li>Facilitate finance applications through third-party providers</li>
              <li>Process job applications submitted by you</li>
            </ul>

            <h3 className="font-semibold text-slate-900 mb-2">b) Legal Obligation</h3>
            <p className="mb-1">We process your data when required to:</p>
            <ul className="list-disc ml-6 space-y-1 mb-4">
              <li>Maintain financial records for HMRC and accounting purposes</li>
              <li>Comply with employment law and regulatory obligations</li>
            </ul>

            <h3 className="font-semibold text-slate-900 mb-2">c) Consent</h3>
            <p className="mb-1">We process your data when:</p>
            <ul className="list-disc ml-6 space-y-1 mb-2">
              <li>You have given clear consent for us to send marketing communications</li>
              <li>You have opted in to receive updates or offers</li>
            </ul>
            <p>You can withdraw your consent at any time &mdash; see section 8 below.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">4. Who We Share Your Data With</h2>
            <p className="mb-2">We do not share your personal data with third parties unless:</p>
            <ul className="list-disc ml-6 space-y-1 mb-4">
              <li>You have provided consent</li>
              <li>It is necessary to provide a service (e.g. finance quotes via third-party lenders or warranty companies)</li>
              <li>We are legally required to do so (e.g. for tax or regulatory reporting)</li>
            </ul>

            <h3 className="font-semibold text-slate-900 mb-2">Data Processors</h3>
            <p className="mb-2">We may use trusted third-party service providers to support our business operations. These may include:</p>
            <ul className="list-disc ml-6 space-y-1 mb-2">
              <li>Accountancy service providers</li>
              <li>Marketing and CRM software providers</li>
              <li>Website and IT system providers</li>
            </ul>
            <p>All data processors are contractually bound to handle your data securely and in line with UK GDPR requirements.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">5. Vehicle Documentation</h2>
            <p>Any documentation received with a vehicle (such as log books, service history, or receipts) may be made available to potential buyers and passed to the new owner to maintain the vehicle's provenance.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">6. Data Retention</h2>
            <p className="mb-2">We retain personal data only as long as necessary:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong>Financial and transaction records:</strong> 7 years (to meet HMRC requirements)</li>
              <li><strong>Marketing data:</strong> until you withdraw consent or unsubscribe</li>
              <li><strong>Job applications:</strong> 6 months (unless employed or otherwise agreed)</li>
              <li><strong>CCTV footage:</strong> usually retained for 30 days, unless required for investigation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">7. Your Rights</h2>
            <p className="mb-2">You have the following rights under the UK GDPR:</p>
            <ul className="list-disc ml-6 space-y-1 mb-3">
              <li><strong>Right to access</strong> &ndash; Request a copy of the personal data we hold about you</li>
              <li><strong>Right to rectification</strong> &ndash; Ask us to correct any inaccurate or incomplete data</li>
              <li><strong>Right to erasure</strong> &ndash; Request deletion of your personal data (in certain cases)</li>
              <li><strong>Right to restrict processing</strong> &ndash; Ask us to pause the use of your data</li>
              <li><strong>Right to object</strong> &ndash; Object to the processing of your data for direct marketing or legitimate interests</li>
              <li><strong>Right to data portability</strong> &ndash; Request your data in a commonly used format</li>
              <li><strong>Right to withdraw consent</strong> &ndash; Withdraw any consent you have given at any time</li>
            </ul>
            <p>To exercise your rights, please contact us using the details provided in section 1.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">8. Right to Complain</h2>
            <p className="mb-3">If you are unhappy with how we have handled your personal data, please contact us directly so we can address your concerns.</p>
            <p className="mb-2">You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):</p>
            <div className="space-y-1">
              <p><strong>Website:</strong> <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.ico.org.uk</a></p>
              <p><strong>Phone:</strong> 0303 123 1113</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">9. Data Transfers Outside the UK</h2>
            <p>We do not routinely transfer personal data outside the UK. If this ever becomes necessary (e.g. through a third-party provider), we will ensure that appropriate safeguards are in place, such as standard contractual clauses or adequacy decisions.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">10. Website and Cookies</h2>
            <p>If you use our website, we may collect technical data such as your IP address and browser information. Cookies may be used to enhance your experience and for basic analytics. For more details, please contact us for more information.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">11. Changes to This Privacy Policy</h2>
            <p>We regularly review this policy to ensure it remains accurate and up to date. Any changes will be posted on our website or made available upon request.</p>
          </section>
        </div>

        <div className="text-center py-6">
          <p className="text-slate-500 text-sm">
            Powered by <a href="https://www.teessideautos.co.uk/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">TeessideAutos</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
